import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import AdminRoomsTab from '../components/AdminRoomsTab';
import Modal from '../components/Modal';
import Spinner from '../components/Spinner';
import {
  adminAddDepartment,
  adminAddUser,
  adminDeleteMeetingPermanentlyV19_1,
  adminDeleteUser,
  adminListDepartments,
  adminListUsers,
  adminPreviewMeetingDeleteV19_1,
  adminUpdateDepartment,
  adminUpdateUser,
  adminUpsertUsers,
  adminUserMeetings,
  correctAttendanceAsStaff,
  staffAdminGetMeetingState,
  staffAdminDeleteDepartment,
  staffAdminGetDepartmentUsage,
  staffAdminListMeetings,
  staffAdminListRooms,
} from '../lib/api';
import { downloadCsv, exportDateStamp } from '../lib/csvExport';
import { parseUserImportFile, type ParsedUserImport } from '../lib/userImport';
import {
  errorMessage,
  formatDate,
  formatDateTime,
  formatTime,
  getMalaysiaTodayISO,
  isMtibEmail,
  isValidEmail,
  normalizeEmail,
} from '../lib/utils';
import type {
  AdminMeetingDeletePreview,
  AdminMeetingRow,
  AdminMeetingState,
  AdminRoom,
  AdminUserMeetingRow,
  AdminUserRow,
  Department,
  UserImportRow,
  UserProfile,
  UserRole,
} from '../types';
import { useStaffAuth } from '../auth/StaffAuthContext';
import { useToast } from '../context/ToastContext';

type AdminTab = 'users' | 'departments' | 'meetings' | 'rooms';
type UserTypeFilter = 'all' | 'internal' | 'guest';

export default function AdminPage() {
  const { identity, refreshProfile } = useStaffAuth();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [meetings, setMeetings] = useState<AdminMeetingRow[]>([]);
  const [rooms, setRooms] = useState<AdminRoom[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [roomsError, setRoomsError] = useState<string | null>(null);

  const [editing, setEditing] = useState<AdminUserRow | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [viewUserMeetings, setViewUserMeetings] = useState<AdminUserRow | null>(null);
  const [meetingDetailId, setMeetingDetailId] = useState<string | null>(null);
  const [meetingDeleteId, setMeetingDeleteId] = useState<string | null>(null);

  const [userSearch, setUserSearch] = useState('');
  const [userType, setUserType] = useState<UserTypeFilter>('all');

  const [meetingSearch, setMeetingSearch] = useState('');
  const [attendeeFilter, setAttendeeFilter] = useState('');
  const [hostFilter, setHostFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try { setUsers(await adminListUsers()); }
    catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setLoadingUsers(false); }
  }, [showToast]);

  const loadDepartments = useCallback(async () => {
    setLoadingDepartments(true);
    try { setDepartments(await adminListDepartments()); }
    catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setLoadingDepartments(false); }
  }, [showToast]);

  const reloadDepartmentConsumers = useCallback(async () => {
    await Promise.all([loadDepartments(), loadUsers(), refreshProfile()]);
  }, [loadDepartments, loadUsers, refreshProfile]);

  const loadMeetings = useCallback(async (filters?: { attendeeEmail?: string; hostEmail?: string; dateFrom?: string; dateTo?: string }) => {
    setLoadingMeetings(true);
    try { setMeetings(await staffAdminListMeetings(filters)); }
    catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setLoadingMeetings(false); }
  }, [showToast]);

  const loadRooms = useCallback(async () => {
    setLoadingRooms(true);
    setRoomsError(null);
    try { setRooms(await staffAdminListRooms()); }
    catch (error) {
      const message = errorMessage(error);
      setRoomsError(message);
      showToast(message, 'error');
    } finally { setLoadingRooms(false); }
  }, [showToast]);

  useEffect(() => {
    if (identity.role !== 'Admin') return;
    void Promise.all([loadUsers(), loadDepartments(), loadMeetings(), loadRooms()]);
  }, [identity.role, loadUsers, loadDepartments, loadMeetings, loadRooms]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    return users.filter((user) => {
      if (userType === 'internal' && !user.is_internal) return false;
      if (userType === 'guest' && user.is_internal) return false;
      if (!q) return true;
      return [user.full_name, user.email, user.department_org ?? '', user.role].some((value) => value.toLowerCase().includes(q));
    });
  }, [users, userSearch, userType]);

  const filteredMeetings = useMemo(() => {
    const q = meetingSearch.trim().toLowerCase();
    if (!q) return meetings;
    return meetings.filter((meeting) => [
      meeting.title,
      meeting.location ?? '',
      meeting.chairman ?? '',
      meeting.host_email,
      meeting.status,
    ].some((value) => value.toLowerCase().includes(q)));
  }, [meetings, meetingSearch]);

  const hostOptions = useMemo(() => users.filter((user) => user.hosted_count > 0), [users]);

  if (identity.role !== 'Admin') {
    return <Layout title="Pentadbiran"><div className="empty-state"><h2>Akses ditolak</h2><p>Panel ini hanya untuk pengguna Admin yang telah log masuk.</p></div></Layout>;
  }

  const exportUsers = () => {
    downloadCsv(`mtib-users-${exportDateStamp()}.csv`, [
      'E-mel', 'Nama Penuh', 'Bahagian / Organisasi', 'Jenis', 'Peranan', 'Didaftarkan', 'Mesyuarat Dihoskan', 'Mesyuarat Dihadiri', 'Mesyuarat Dijadualkan',
    ], filteredUsers.map((user) => [
      user.email,
      user.full_name,
      user.department_org ?? '',
      user.is_internal ? 'Internal' : 'Guest',
      user.role,
      user.registered_at,
      user.hosted_count,
      user.attended_count,
      user.scheduled_count,
    ]));
  };

  const exportMeetings = () => {
    downloadCsv(`mtib-meetings-${exportDateStamp()}.csv`, [
      'ID Mesyuarat', 'Tajuk', 'Tarikh', 'Waktu', 'Lokasi', 'Pengerusi', 'E-mel Host', 'Nama Host', 'Status', 'Dijemput', 'Check-in Sah', 'Dalaman', 'Tetamu', 'Dibatalkan', 'Dicipta Pada',
    ], filteredMeetings.map((meeting) => [
      meeting.meeting_id,
      meeting.title,
      meeting.meeting_date,
      meeting.meeting_time,
      meeting.location ?? '',
      meeting.chairman ?? '',
      meeting.host_email,
      meeting.host_full_name ?? '',
      meeting.status,
      meeting.expected_count,
      meeting.checked_in_count,
      meeting.internal_count,
      meeting.guest_count,
      meeting.invalid_count ?? 0,
      meeting.created_at,
    ]));
  };

  const applyMeetingFilters = async () => {
    await loadMeetings({
      attendeeEmail: attendeeFilter || undefined,
      hostEmail: hostFilter || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });
  };

  const resetMeetingFilters = async () => {
    setAttendeeFilter('');
    setHostFilter('');
    setDateFrom('');
    setDateTo('');
    setMeetingSearch('');
    await loadMeetings();
  };

  return <Layout title="Pentadbiran">
    <div className="security-callout"><strong>Pengurusan Admin sahaja</strong><p>Semua data pada halaman ini diperoleh melalui fungsi pangkalan data untuk Admin yang disahkan. Pengguna biasa dan tetamu tidak boleh mengakses senarai ini.</p></div>

    <div className="admin-main-tabs" role="tablist" aria-label="Bahagian pentadbiran">
      <button id="admin-tab-users" className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')} role="tab" aria-selected={activeTab === 'users'} aria-controls="admin-panel-users">Pengguna <span>{users.length}</span></button>
      <button id="admin-tab-departments" className={activeTab === 'departments' ? 'active' : ''} onClick={() => setActiveTab('departments')} role="tab" aria-selected={activeTab === 'departments'} aria-controls="admin-panel-departments">Bahagian <span>{departments.length}</span></button>
      <button id="admin-tab-meetings" className={activeTab === 'meetings' ? 'active' : ''} onClick={() => setActiveTab('meetings')} role="tab" aria-selected={activeTab === 'meetings'} aria-controls="admin-panel-meetings">Mesyuarat <span>{meetings.length}</span></button>
      <button id="admin-tab-rooms" className={activeTab === 'rooms' ? 'active' : ''} onClick={() => setActiveTab('rooms')} role="tab" aria-selected={activeTab === 'rooms'} aria-controls="admin-panel-rooms">Bilik <span>{rooms.length}</span></button>
    </div>

    <section id="admin-panel-users" className="section-block admin-section" role="tabpanel" aria-labelledby="admin-tab-users" hidden={activeTab !== 'users'}>
    {activeTab === 'users' && <>
      <div className="section-toolbar">
        <div><h2>Pengguna</h2><p className="muted">Urus profil dan semak aktiviti mesyuarat setiap pengguna.</p></div>
        <div className="action-row">
          <button className="button button-ghost button-small" onClick={exportUsers} disabled={!filteredUsers.length}>↓ Eksport CSV</button>
          <button className="button button-secondary button-small" onClick={() => setImportOpen(true)}>⇧ Import CSV / Excel</button>
          <button className="button button-primary button-small" onClick={() => setAddOpen(true)}>+ Tambah pengguna</button>
        </div>
      </div>
      <div className="admin-filter-bar admin-filter-users">
        <label className="field compact-field"><span>Cari pengguna</span><input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Nama, e-mel atau bahagian" /></label>
        <label className="field compact-field"><span>Jenis</span><select value={userType} onChange={(e) => setUserType(e.target.value as UserTypeFilter)}><option value="all">Semua pengguna</option><option value="internal">Dalaman MTIB</option><option value="guest">Tetamu / Luar</option></select></label>
        <div className="filter-result-count"><strong>{filteredUsers.length}</strong><span>dipaparkan</span></div>
      </div>
      {loadingUsers ? <Spinner label="Sedang memuatkan pengguna…" /> : <div className="table-wrap"><table className="data-table admin-table"><thead><tr><th>Pengguna</th><th>Jenis</th><th>Bahagian / Organisasi</th><th>Peranan</th><th>Aktiviti mesyuarat</th><th>Didaftarkan</th><th /></tr></thead><tbody>
        {filteredUsers.length ? filteredUsers.map((user) => <tr key={user.email}>
          <td><strong>{user.full_name}</strong><small>{user.email}</small></td>
          <td><span className={`type-badge ${user.is_internal ? 'internal' : 'guest'}`}>{user.is_internal ? 'Dalaman' : 'Tetamu'}</span></td>
          <td>{user.department_org ?? 'Bahagian belum ditetapkan'}</td>
          <td>{user.role}</td>
          <td><div className="mini-stat-row"><span><strong>{user.hosted_count}</strong> dihoskan</span><span><strong>{user.attended_count}</strong> dihadiri</span><span><strong>{user.scheduled_count}</strong> dijadualkan</span></div></td>
          <td>{formatDateTime(user.registered_at)}</td>
          <td><div className="table-actions"><button onClick={() => setViewUserMeetings(user)}>Mesyuarat</button><button onClick={() => setEditing(user)}>Sunting</button><button className="danger-link" onClick={async () => {
            if (!window.confirm(`Padam pengguna ${user.email}? Rekod kehadiran terdahulu tidak berubah.`)) return;
            try { await adminDeleteUser(user.email); showToast('Pengguna dipadam.', 'success'); await loadUsers(); }
            catch (error) { showToast(errorMessage(error), 'error'); }
          }}>Padam</button></div></td>
        </tr>) : <tr><td colSpan={7} className="table-empty">Tiada pengguna yang sepadan dengan tapisan ini.</td></tr>}
      </tbody></table></div>}
    </>}
    </section>

    <div id="admin-panel-departments" role="tabpanel" aria-labelledby="admin-tab-departments" hidden={activeTab !== 'departments'}>
      {activeTab === 'departments' && <DepartmentsTab departments={departments} loading={loadingDepartments} onReload={reloadDepartmentConsumers} />}
    </div>

    <section id="admin-panel-meetings" className="section-block admin-section" role="tabpanel" aria-labelledby="admin-tab-meetings" hidden={activeTab !== 'meetings'}>
    {activeTab === 'meetings' && <>
      <div className="section-toolbar">
        <div><h2>Mesyuarat</h2><p className="muted">Senarai semua mesyuarat dengan tapisan kehadiran, <em>Host</em> dan tarikh.</p></div>
        <button className="button button-ghost button-small" onClick={exportMeetings} disabled={!filteredMeetings.length}>↓ Eksport CSV</button>
      </div>
      <div className="admin-filter-bar admin-filter-meetings">
        <label className="field compact-field"><span>Cari</span><input value={meetingSearch} onChange={(e) => setMeetingSearch(e.target.value)} placeholder="Tajuk, nama atau e-mel Host" /></label>
        <label className="field compact-field"><span>Ahli (telah hadir)</span><select value={attendeeFilter} onChange={(e) => setAttendeeFilter(e.target.value)}><option value="">Semua ahli</option>{users.map((user) => <option key={user.email} value={user.email}>{user.full_name} — {user.email}</option>)}</select></label>
        <label className="field compact-field"><span><em>Host</em></span><select value={hostFilter} onChange={(e) => setHostFilter(e.target.value)}><option value="">Semua Host</option>{hostOptions.map((user) => <option key={user.email} value={user.email}>{user.full_name} — {user.email}</option>)}</select></label>
        <label className="field compact-field"><span>Tarikh mula</span><input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></label>
        <label className="field compact-field"><span>Tarikh tamat</span><input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></label>
        <div className="filter-actions"><button className="button button-primary button-small" onClick={() => void applyMeetingFilters()}>Guna</button><button className="button button-ghost button-small" onClick={() => void resetMeetingFilters()}>Set Semula</button></div>
      </div>
      {loadingMeetings ? <Spinner label="Sedang memuatkan mesyuarat…" /> : <div className="table-wrap"><table className="data-table admin-table"><thead><tr><th>Tarikh / Waktu</th><th>Mesyuarat</th><th>Lokasi</th><th>Pengerusi</th><th><em>Host</em></th><th>Dijemput</th><th>Telah Hadir</th><th>Status</th><th>Dicipta</th><th /></tr></thead><tbody>
        {filteredMeetings.length ? filteredMeetings.map((meeting) => <tr key={meeting.meeting_id}>
          <td><strong>{formatDate(meeting.meeting_date)}</strong><small>{formatTime(meeting.meeting_time)}</small></td>
          <td><strong>{meeting.title}</strong><small>{meeting.meeting_id}</small></td>
          <td>{meeting.location || '—'}</td>
          <td>{meeting.chairman || '—'}</td>
          <td><strong>{meeting.host_full_name ?? users.find((user) => user.email.toLowerCase() === meeting.host_email.toLowerCase())?.full_name ?? meeting.host_email}</strong><small>{meeting.host_email}</small></td>
          <td>{meeting.expected_count}</td>
          <td><strong>{meeting.checked_in_count}</strong><small>{meeting.internal_count} dalaman · {meeting.guest_count} tetamu{(meeting.invalid_count ?? 0) > 0 ? ` · ${meeting.invalid_count} dibatalkan` : ''}</small></td>
          <td><span className={`status-pill ${meeting.status === 'Active' ? 'status-active' : 'status-cancelled'}`}>{meeting.status}</span></td>
          <td>{formatDateTime(meeting.created_at)}</td>
          <td><div className="table-actions"><button onClick={() => setMeetingDetailId(meeting.meeting_id)}>Lihat butiran</button><button className="danger-link" onClick={() => setMeetingDeleteId(meeting.meeting_id)}>Padam Secara Kekal</button></div></td>
        </tr>) : <tr><td colSpan={10} className="table-empty">Tiada mesyuarat yang sepadan dengan tapisan ini.</td></tr>}
      </tbody></table></div>}
    </>}
    </section>

    <div id="admin-panel-rooms" role="tabpanel" aria-labelledby="admin-tab-rooms" hidden={activeTab !== 'rooms'}>
      {activeTab === 'rooms' && <AdminRoomsTab rooms={rooms} loading={loadingRooms} error={roomsError} onReload={loadRooms} />}
    </div>

    {addOpen && <AddUserModal departments={departments} onClose={() => setAddOpen(false)} onSaved={async () => { setAddOpen(false); await loadUsers(); }} />}
    {importOpen && <ImportUsersModal departments={departments} existingEmails={new Set(users.map((user) => user.email.toLowerCase()))} onClose={() => setImportOpen(false)} onImported={async () => { setImportOpen(false); await loadUsers(); }} />}
    {editing && <UserEditModal user={editing} departments={departments} onClose={() => setEditing(null)} onSaved={async (updated) => {
      setEditing(null);
      if (updated.email.toLowerCase() === identity.email?.toLowerCase()) await refreshProfile();
      await loadUsers();
    }} />}
    {viewUserMeetings && <UserMeetingsModal user={viewUserMeetings} onClose={() => setViewUserMeetings(null)} onViewMeeting={(meetingId) => { setViewUserMeetings(null); setMeetingDetailId(meetingId); }} />}
    {meetingDetailId && <AdminMeetingDetailModal meetingId={meetingDetailId} onClose={() => setMeetingDetailId(null)} />}
    {meetingDeleteId && <AdminPermanentMeetingDeleteModal meetingId={meetingDeleteId} onClose={() => setMeetingDeleteId(null)} onDeleted={async (deletedMeetingId) => {
      setMeetingDeleteId(null);
      if (meetingDetailId === deletedMeetingId) setMeetingDetailId(null);
      await Promise.all([
        loadMeetings({
          attendeeEmail: attendeeFilter || undefined,
          hostEmail: hostFilter || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        }),
        loadRooms(),
      ]);
      showToast('Mesyuarat telah dipadam secara kekal.', 'success');
    }} />}
  </Layout>;
}

function DepartmentsTab({ departments, loading, onReload }: { departments: Department[]; loading: boolean; onReload: () => Promise<void>; }) {
  const { showToast } = useToast();
  const [deptName, setDeptName] = useState('');
  const [deptOrder, setDeptOrder] = useState('');
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usageLoadingId, setUsageLoadingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ department: Department; staffCount: number } | null>(null);
  const [drafts, setDrafts] = useState<Department[]>(departments);

  useEffect(() => { setDrafts(departments); }, [departments]);

  const dirtyDepartments = drafts.filter((draft) => {
    const original = departments.find((department) => department.department_id === draft.department_id);
    return original && (draft.department_name !== original.department_name || draft.sort_order !== original.sort_order);
  });

  const addDepartment = async (event: FormEvent) => {
    event.preventDefault();
    if (deptName.trim().length < 2) return;
    const order = deptOrder ? Number(deptOrder) : undefined;
      if (order !== undefined && (!Number.isInteger(order) || order <= 0)) { showToast('Nombor susunan mestilah nombor bulat positif.', 'error'); return; }
    setAdding(true);
    try {
      await adminAddDepartment(deptName, order);
      setDeptName('');
      setDeptOrder('');
      showToast('Bahagian ditambah.', 'success');
      await onReload();
    } catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setAdding(false); }
  };

  const updateDraft = (departmentId: number, patch: Partial<Department>) => {
    setDrafts((current) => current.map((department) => department.department_id === departmentId ? { ...department, ...patch } : department));
  };

  const saveChanges = async () => {
    if (!dirtyDepartments.length) return;
    if (dirtyDepartments.some((department) => !Number.isInteger(department.sort_order) || department.sort_order <= 0)) {
      showToast('Setiap nombor susunan yang diubah mestilah nombor bulat positif.', 'error');
      return;
    }
    if (dirtyDepartments.some((department) => department.department_name.trim().length < 2)) {
      showToast('Setiap bahagian yang diubah memerlukan nama yang sah.', 'error');
      return;
    }
    setSaving(true);
    try {
      await Promise.all(dirtyDepartments.map((department) => adminUpdateDepartment({
        ...department,
        department_name: department.department_name.trim(),
      })));
      showToast('Perubahan bahagian disimpan.', 'success');
      await onReload();
    } catch (error) {
      showToast(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const previewDepartmentDelete = async (department: Department) => {
    setUsageLoadingId(department.department_id);
    try {
      const usage = await staffAdminGetDepartmentUsage(department.department_id);
      const currentDepartment = departments.find((item) => item.department_id === department.department_id) ?? department;
      setDeleteTarget({ department: currentDepartment, staffCount: usage.staff_count });
    } catch (error) {
      showToast(errorMessage(error), 'error');
    } finally {
      setUsageLoadingId(null);
    }
  };

  return <section className="section-block admin-section">
    <div className="section-toolbar"><div><h2>Bahagian</h2><p className="muted">Urus senarai bahagian MTIB mengikut susunan untuk pendaftaran.</p></div><button type="button" className="button button-primary button-small" disabled={!dirtyDepartments.length || saving} onClick={() => void saveChanges()}>{saving ? 'Sedang menyimpan…' : 'Simpan perubahan'}</button></div>
    <form onSubmit={addDepartment} className="department-add-form">
      <label className="field compact-field"><span>No. susunan</span><input type="number" min="1" value={deptOrder} onChange={(e) => setDeptOrder(e.target.value)} placeholder="Automatik" /></label>
      <label className="field compact-field"><span>Nama bahagian</span><input value={deptName} onChange={(e) => setDeptName(e.target.value)} placeholder="Nama bahagian baharu" /></label>
      <button className="button button-primary button-small" disabled={adding}>{adding ? 'Sedang menambah…' : '+ Tambah bahagian'}</button>
    </form>
    {loading ? <Spinner label="Sedang memuatkan bahagian…" /> : <div className="table-wrap"><table className="data-table department-table"><thead><tr><th>No. susunan</th><th>Nama bahagian</th><th /></tr></thead><tbody>{drafts.map((department) => <tr key={department.department_id}><td><input className="order-input" type="number" min="1" value={department.sort_order} onChange={(event) => updateDraft(department.department_id, { sort_order: Number(event.target.value) })} /></td><td><input value={department.department_name} onChange={(event) => updateDraft(department.department_id, { department_name: event.target.value })} /></td><td><div className="table-actions"><button type="button" disabled={saving || usageLoadingId !== null} className="danger-link" onClick={() => void previewDepartmentDelete(department)}>{usageLoadingId === department.department_id ? 'Sedang menyemak…' : 'Padam'}</button></div></td></tr>)}</tbody></table></div>}
    {deleteTarget && <DeleteDepartmentModal
      key={deleteTarget.department.department_id}
      department={deleteTarget.department}
      initialStaffCount={deleteTarget.staffCount}
      departments={departments}
      onClose={() => setDeleteTarget(null)}
      onDeleted={async () => {
        setDeleteTarget(null);
        await onReload();
      }}
    />}
  </section>;
}

function DeleteDepartmentModal({
  department,
  initialStaffCount,
  departments,
  onClose,
  onDeleted,
}: {
  department: Department;
  initialStaffCount: number;
  departments: Department[];
  onClose: () => void;
  onDeleted: () => Promise<void>;
}) {
  const { showToast } = useToast();
  const [staffCount, setStaffCount] = useState(initialStaffCount);
  const [replacementId, setReplacementId] = useState('');
  const [busy, setBusy] = useState(false);
  const replacementDepartments = departments.filter((item) => item.department_id !== department.department_id);
  const requiresReplacement = staffCount > 0;

  const confirmDelete = async () => {
    const selectedReplacementId = replacementId ? Number(replacementId) : null;
    if (requiresReplacement && (!selectedReplacementId || selectedReplacementId === department.department_id)) {
      showToast('Choose a valid replacement department.', 'error');
      return;
    }

    setBusy(true);
    try {
      const result = await staffAdminDeleteDepartment(department.department_id, selectedReplacementId);
      const replacement = replacementDepartments.find((item) => item.department_id === result.replacement_department_id);
      showToast(
        result.moved_staff_count > 0
          ? `${result.moved_staff_count} staf dipindahkan ke ${replacement?.department_name ?? 'bahagian pengganti'} dan bahagian dipadam.`
          : 'Bahagian dipadam.',
        'success',
      );
      await onDeleted();
    } catch (error) {
      showToast(errorMessage(error), 'error');
      try {
        const latestUsage = await staffAdminGetDepartmentUsage(department.department_id);
        setStaffCount(latestUsage.staff_count);
      } catch {
        // Preserve the original operation error; a subsequent list refresh can
        // reconcile a department removed by another Admin.
      }
    } finally {
      setBusy(false);
    }
  };

  return <Modal title={`Padam “${department.department_name}”?`} onClose={() => { if (!busy) onClose(); }}>
    <div className="form-stack">
      {requiresReplacement ? <>
        <div className="security-callout compact">
          <strong>{staffCount} staf sedang ditetapkan kepada bahagian ini.</strong>
          <p>Bahagian ini tidak boleh dipadam sehingga staf dipindahkan ke bahagian lain.</p>
        </div>
        <label className="field">
          <span>Pindahkan {staffCount} staf ke</span>
          <select value={replacementId} onChange={(event) => setReplacementId(event.target.value)} required>
            <option value="">Pilih bahagian pengganti</option>
            {replacementDepartments.map((item) => <option key={item.department_id} value={item.department_id}>{item.department_name}</option>)}
          </select>
        </label>
        <p className="muted">Rekod kehadiran sejarah tidak akan berubah.</p>
      </> : <p>Tiada staf sedang ditetapkan kepada bahagian ini.</p>}
      <div className="form-actions">
        <button type="button" className="button button-ghost" disabled={busy} onClick={onClose}>Batal</button>
        <button type="button" className="button button-danger" disabled={busy || (requiresReplacement && (!replacementId || !replacementDepartments.length))} onClick={() => void confirmDelete()}>{busy ? 'Sedang memproses…' : requiresReplacement ? 'Pindahkan staf & padam' : 'Padam bahagian'}</button>
      </div>
    </div>
  </Modal>;
}

function AddUserModal({ departments, onClose, onSaved }: { departments: Department[]; onClose: () => void; onSaved: () => void; }) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [departmentOrg, setDepartmentOrg] = useState('');
  const [role, setRole] = useState<UserRole>('User');
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();
  const internal = isMtibEmail(email);

  useEffect(() => {
    if (internal && departments.length && !departments.some((d) => d.department_id.toString() === departmentId)) {
      setDepartmentId(departments[0].department_id.toString());
    }
    if (!internal && departmentId) setDepartmentId('');
  }, [internal, departments, departmentId]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const normalized = normalizeEmail(email);
    if (!isValidEmail(normalized)) { showToast('Masukkan e-mel yang sah.', 'error'); return; }
    if (role === 'Admin' && !isMtibEmail(normalized)) { showToast('Peranan Admin memerlukan e-mel @mtib.gov.my.', 'error'); return; }
    const selectedDepartmentId = internal ? Number(departmentId) : null;
    if (fullName.trim().length < 2
      || (internal && (!Number.isInteger(selectedDepartmentId) || Number(selectedDepartmentId) <= 0))
      || (!internal && departmentOrg.trim().length < 2)) {
      showToast('Masukkan nama penuh dan bahagian / organisasi.', 'error');
      return;
    }
    setBusy(true);
    try {
      await adminAddUser({
        email: normalized,
        full_name: fullName.trim(),
        department_id: selectedDepartmentId,
        department_org: internal ? '' : departmentOrg.trim(),
        role,
      });
      showToast(role === 'Admin' ? 'Admin profile saved. The matching MTIB email must use staff numeric-code sign-in.' : 'User added.', 'success');
      await onSaved();
    } catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); }
  };

  return <Modal title="Tambah Pengguna" onClose={onClose}><form className="form-stack" onSubmit={submit}>
    <label className="field"><span>E-mel</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
    <label className="field"><span>Nama penuh</span><input value={fullName} onChange={(e) => setFullName(e.target.value)} maxLength={120} required /></label>
    <label className="field"><span>Bahagian / Organisasi</span>{internal ? <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} required><option value="">Pilih bahagian</option>{departments.map((d) => <option key={d.department_id} value={d.department_id}>{d.department_name}</option>)}</select> : <input value={departmentOrg} onChange={(e) => setDepartmentOrg(e.target.value)} maxLength={160} placeholder="Syarikat / organisasi" required />}</label>
    <label className="field"><span>Peranan</span><select value={role} onChange={(e) => setRole(e.target.value as UserRole)}><option value="User">Pengguna</option><option value="Admin" disabled={!internal}>Admin</option></select>{role === 'Admin' && <small>Admin memerlukan e-mel MTIB yang sama dalam Supabase Auth dan menggunakan log masuk kod angka.</small>}</label>
    <div className="form-actions"><button type="button" className="button button-ghost" onClick={onClose}>Batal</button><button className="button button-primary" disabled={busy}>{busy ? 'Sedang menyimpan…' : 'Tambah pengguna'}</button></div>
  </form></Modal>;
}

function ImportUsersModal({ departments, existingEmails, onClose, onImported }: { departments: Department[]; existingEmails: Set<string>; onClose: () => void; onImported: () => void; }) {
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ParsedUserImport | null>(null);
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();

  const adminRows = useMemo(() => parsed?.rows.filter((row) => row.role === 'Admin').length ?? 0, [parsed]);
  const existingRows = useMemo(() => parsed?.rows.filter((row) => existingEmails.has(row.email.toLowerCase())).length ?? 0, [parsed, existingEmails]);

  const chooseFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParsed(null);
    try { setParsed(await parseUserImportFile(file, departments)); }
    catch (error) { showToast(errorMessage(error), 'error'); event.target.value = ''; setFileName(''); }
  };

  const importRows = async () => {
    if (!parsed?.rows.length) return;
    if (parsed.issues.length) { showToast('Betulkan baris yang tidak sah sebelum mengimport.', 'error'); return; }
    setBusy(true);
    try {
      const count = await adminUpsertUsers(parsed.rows);
      showToast(`${count} profil pengguna berjaya diimport atau dikemas kini.`, 'success');
      await onImported();
    } catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); }
  };

  return <Modal title="Import Pengguna" onClose={onClose} size="wide">
    <div className="form-stack">
      <div className="import-help"><strong>Fail yang diterima</strong><p>CSV atau Excel .xlsx. Lajur wajib: <code>email</code>, <code>full_name</code>, <code>department_org</code>. Lajur pilihan: <code>role</code> (User/Admin). E-mel MTIB mesti menggunakan nama bahagian rasmi dengan tepat.</p><div className="action-row"><a className="button button-ghost button-small" href="/MTIB-User-Import-Template.csv" download>Muat turun templat CSV</a><a className="button button-ghost button-small" href="/MTIB-User-Import-Template.xlsx" download>Muat turun templat Excel</a></div></div>
      <label className="field"><span>Pilih fail</span><input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={chooseFile} /></label>
      {fileName && <small className="muted">Dipilih: {fileName}</small>}
      {parsed && <>
        <div className="import-summary"><span><strong>{parsed.rows.length}</strong> sah</span><span><strong>{parsed.issues.length}</strong> tidak sah</span><span><strong>{existingRows}</strong> sedia ada/dikemas kini</span>{adminRows > 0 && <span><strong>{adminRows}</strong> Admin</span>}</div>
        {parsed.issues.length > 0 && <div className="import-issues"><strong>Baris yang perlu dibetulkan</strong>{parsed.issues.slice(0, 15).map((issue) => <div key={`${issue.row}-${issue.message}`}>Baris {issue.row}: {issue.message}</div>)}{parsed.issues.length > 15 && <small>…dan {parsed.issues.length - 15} lagi.</small>}</div>}
        {parsed.rows.length > 0 && <div className="table-wrap import-preview"><table className="data-table"><thead><tr><th>E-mel</th><th>Nama</th><th>Bahagian / Organisasi</th><th>Peranan</th><th>Status</th></tr></thead><tbody>{parsed.rows.slice(0, 12).map((row) => <tr key={row.email}><td>{row.email}</td><td>{row.full_name}</td><td>{row.department_org}</td><td>{row.role}</td><td>{existingEmails.has(row.email.toLowerCase()) ? 'Kemas kini' : 'Baharu'}</td></tr>)}</tbody></table>{parsed.rows.length > 12 && <small className="muted">Pratonton 12 daripada {parsed.rows.length} baris yang sah.</small>}</div>}
        {adminRows > 0 && <div className="security-callout compact"><strong>Baris Admin</strong><p>Baris Admin mesti menggunakan e-mel MTIB. Import hanya mencipta atau mengemas kini profil Kehadiran; e-mel yang sama mesti wujud dalam Supabase Auth dan menggunakan log masuk kod angka.</p></div>}
      </>}
      <div className="form-actions"><button type="button" className="button button-ghost" onClick={onClose}>Batal</button><button type="button" className="button button-primary" disabled={busy || !parsed?.rows.length || Boolean(parsed?.issues.length)} onClick={importRows}>{busy ? 'Sedang mengimport…' : 'Import pengguna'}</button></div>
    </div>
  </Modal>;
}

function UserEditModal({ user, departments, onClose, onSaved }: { user: AdminUserRow; departments: Department[]; onClose: () => void; onSaved: (profile: UserProfile) => void; }) {
  const [email, setEmail] = useState(user.email);
  const [fullName, setFullName] = useState(user.full_name);
  const [departmentId, setDepartmentId] = useState(user.department_id?.toString() ?? '');
  const [departmentOrg, setDepartmentOrg] = useState(user.is_internal ? '' : user.department_org ?? '');
  const [role, setRole] = useState<UserRole>(user.role);
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();
  const internal = isMtibEmail(email);

  useEffect(() => {
    if (internal && departments.length && !departments.some((d) => d.department_id.toString() === departmentId)) {
      setDepartmentId('');
    }
    if (!internal && departmentId) setDepartmentId('');
  }, [internal, departments, departmentId]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const normalized = normalizeEmail(email);
    if (!isValidEmail(normalized)) { showToast('Masukkan e-mel yang sah.', 'error'); return; }
    if (role === 'Admin' && !isMtibEmail(normalized)) { showToast('Peranan Admin memerlukan e-mel @mtib.gov.my.', 'error'); return; }
    const selectedDepartmentId = internal ? Number(departmentId) : null;
    if (fullName.trim().length < 2
      || (internal && (!Number.isInteger(selectedDepartmentId) || Number(selectedDepartmentId) <= 0))
      || (!internal && departmentOrg.trim().length < 2)) {
      showToast('Masukkan nama penuh dan bahagian / organisasi.', 'error');
      return;
    }
    setBusy(true);
    try {
      const selectedDepartmentName = departments.find((item) => item.department_id === selectedDepartmentId)?.department_name ?? null;
      const row: UserImportRow = {
        email: normalized,
        full_name: fullName.trim(),
        department_id: selectedDepartmentId,
        department_org: internal ? '' : departmentOrg.trim(),
        role,
      };
      await adminUpdateUser(user.email, row);
      showToast('Pengguna dikemas kini. Petikan sejarah kehadiran tidak berubah.', 'success');
      onSaved({
        ...row,
        department_org: internal ? selectedDepartmentName : row.department_org,
        registered_at: user.registered_at,
        is_internal: internal,
      });
    } catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); }
  };

  return <Modal title="Sunting Pengguna" onClose={onClose}><form className="form-stack" onSubmit={submit}>
    <label className="field"><span>E-mel</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={user.role === 'Admin'} required />{user.role === 'Admin' && <small>E-mel Admin mesti kekal dipautkan kepada akaun Supabase Authentication.</small>}</label>
    <label className="field"><span>Nama penuh</span><input value={fullName} onChange={(e) => setFullName(e.target.value)} required /></label>
    <label className="field"><span>Bahagian / Organisasi</span>{internal ? <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} required><option value="">Pilih bahagian</option>{departments.map((d) => <option key={d.department_id} value={d.department_id}>{d.department_name}</option>)}</select> : <input value={departmentOrg} onChange={(e) => setDepartmentOrg(e.target.value)} required />}</label>
    <label className="field"><span>Peranan</span><select value={role} onChange={(e) => setRole(e.target.value as UserRole)}><option value="User">Pengguna</option><option value="Admin" disabled={!internal}>Admin</option></select></label>
    <div className="form-actions"><button type="button" className="button button-ghost" onClick={onClose}>Batal</button><button className="button button-primary" disabled={busy}>{busy ? 'Sedang menyimpan…' : 'Simpan perubahan'}</button></div>
  </form></Modal>;
}

function UserMeetingsModal({ user, onClose, onViewMeeting }: { user: AdminUserRow; onClose: () => void; onViewMeeting: (meetingId: string) => void; }) {
  const [rows, setRows] = useState<AdminUserMeetingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void adminUserMeetings(user.email).then((data) => { if (!cancelled) setRows(data); }).catch((error) => { if (!cancelled) showToast(errorMessage(error), 'error'); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.email, showToast]);

  const today = getMalaysiaTodayISO();
  const scheduled = rows.filter((row) => row.status === 'Active' && row.meeting_date >= today && (row.is_host || row.is_expected)).length;
  const attended = rows.filter((row) => row.has_attended).length;
  const hosted = rows.filter((row) => row.is_host).length;

  return <Modal title={`Mesyuarat — ${user.full_name}`} onClose={onClose} size="wide">
    <div className="user-meeting-summary"><article><span>Dihoskan</span><strong>{hosted}</strong></article><article><span>Dihadiri</span><strong>{attended}</strong></article><article><span>Dijadualkan</span><strong>{scheduled}</strong></article></div>
    <p className="muted modal-intro">{user.email} · {user.department_org ?? 'Bahagian belum ditetapkan'}</p>
    {loading ? <Spinner label="Sedang memuatkan mesyuarat pengguna…" /> : <div className="table-wrap"><table className="data-table"><thead><tr><th>Tarikh / Waktu</th><th>Mesyuarat</th><th>Hubungan</th><th><em>Host</em></th><th>Check-in</th><th>Status</th><th /></tr></thead><tbody>
      {rows.length ? rows.map((row) => <tr key={row.meeting_id}><td><strong>{formatDate(row.meeting_date)}</strong><small>{formatTime(row.meeting_time)}</small></td><td><strong>{row.title}</strong><small>{row.location || 'Lokasi belum dinyatakan'}</small></td><td><div className="relation-badges">{row.is_host && <span>Dihoskan</span>}{row.has_attended && <span>Dihadiri</span>}{row.is_expected && !row.has_attended && <span>Dijadualkan</span>}</div></td><td>{row.host_email}</td><td>{row.checkin_time ? formatDateTime(row.checkin_time) : '—'}</td><td><span className={`status-pill ${row.status === 'Active' ? 'status-active' : 'status-cancelled'}`}>{row.status}</span></td><td><div className="table-actions"><button onClick={() => onViewMeeting(row.meeting_id)}>Lihat</button></div></td></tr>) : <tr><td colSpan={7} className="table-empty">Tiada mesyuarat dihoskan, dihadiri atau akan datang yang dijadualkan untuk pengguna ini.</td></tr>}
    </tbody></table></div>}
  </Modal>;
}

function AdminMeetingDetailModal({ meetingId, onClose }: { meetingId: string; onClose: () => void; }) {
  const [state, setState] = useState<AdminMeetingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [correctionTarget, setCorrectionTarget] = useState<{ record: AdminMeetingState['attendance'][number]; action: 'INVALIDATE' | 'RESTORE' } | null>(null);
  const { showToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try { setState(await staffAdminGetMeetingState(meetingId)); }
    catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setLoading(false); }
  }, [meetingId, showToast]);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <Modal title="Butiran Mesyuarat" onClose={onClose} size="wide"><Spinner label="Sedang memuatkan butiran mesyuarat…" /></Modal>;
  if (!state) return <Modal title="Butiran Mesyuarat" onClose={onClose}><div className="empty-state compact">Butiran mesyuarat tidak dapat dimuatkan.</div></Modal>;

  const validAttendance = state.attendance.filter((row) => row.is_valid !== false);
  const invalidAttendance = state.attendance.filter((row) => row.is_valid === false);
  const internal = validAttendance.filter((row) => row.is_internal).length;
  const guests = validAttendance.length - internal;

  return <Modal title={state.meeting.title} onClose={onClose} size="wide">
    <div className="admin-meeting-detail-grid">
      <div><span>Tarikh</span><strong>{formatDate(state.meeting.meeting_date)}</strong></div>
      <div><span>Waktu</span><strong>{formatTime(state.meeting.meeting_time)}</strong></div>
      <div><span>Lokasi</span><strong>{state.meeting.location || '—'}</strong></div>
      <div><span>Pengerusi</span><strong>{state.meeting.chairman || '—'}</strong></div>
      <div><span><em>Host</em></span><strong>{state.meeting.host_email}</strong></div>
      <div><span>Status</span><strong>{state.meeting.status}</strong></div>
    </div>
    <div className="user-meeting-summary admin-meeting-counts"><article><span>Dijemput</span><strong>{state.expected.length}</strong></article><article><span>Check-in sah</span><strong>{validAttendance.length}</strong></article><article><span>Dalaman / Tetamu</span><strong>{internal} / {guests}</strong></article><article><span>Dibatalkan</span><strong>{invalidAttendance.length}</strong></article></div>

    <div className="admin-detail-section"><h3>Senarai Kehadiran</h3><div className="table-wrap"><table className="data-table"><thead><tr><th>Peserta</th><th>Bahagian / Organisasi</th><th>Jenis</th><th>Status / audit</th><th>Check-in</th><th>Kaedah</th><th /></tr></thead><tbody>
      {state.attendance.length ? state.attendance.map((row) => <tr key={row.record_id}><td><strong>{row.snapshot_name}</strong><small>{row.user_email}</small></td><td>{row.snapshot_dept}</td><td>{row.is_internal ? 'Dalaman' : 'Tetamu'}</td><td><span className={`roster-status ${row.is_valid === false ? 'invalid' : 'checked'}`}>{row.is_valid === false ? 'Dibatalkan' : 'Sah'}</span>{row.corrections?.map((correction) => <small key={correction.correction_id}>{correction.action === 'INVALIDATE' ? `Dibatalkan: ${correction.reason}` : 'Dipulihkan'} oleh {correction.actor_email} · {formatDateTime(correction.created_at)}{correction.note ? ` · ${correction.note}` : ''}</small>)}</td><td>{formatDateTime(row.checkin_time)}</td><td>{row.checkin_method}</td><td><button className="button button-ghost button-small" onClick={() => setCorrectionTarget({ record: row, action: row.is_valid === false ? 'RESTORE' : 'INVALIDATE' })}>{row.is_valid === false ? 'Pulihkan' : 'Batalkan'}</button></td></tr>) : <tr><td colSpan={7} className="table-empty">Tiada kehadiran direkodkan.</td></tr>}
    </tbody></table></div></div>

    <div className="admin-detail-section"><h3>Ahli Dijemput</h3><div className="table-wrap"><table className="data-table"><thead><tr><th>Nama / E-mel</th><th>Profil dipadankan</th><th>Bahagian / Organisasi</th><th>Jenis</th></tr></thead><tbody>
      {state.expected.length ? state.expected.map((row) => <tr key={row.expected_id}><td>{row.name_or_email}</td><td>{row.resolved_name || '—'}</td><td>{row.resolved_department_org || '—'}</td><td>{row.resolved_is_internal === null ? 'Tidak diketahui' : row.resolved_is_internal ? 'Dalaman' : 'Tetamu'}</td></tr>) : <tr><td colSpan={4} className="table-empty">Tiada ahli dijemput dinyatakan.</td></tr>}
    </tbody></table></div></div>

    <p className="tiny-note">Pembetulan hanya ditambah sebagai rekod baharu. Rekod kehadiran asal dan waktu check-in tidak pernah diubah.</p>
    <p className="tiny-note">ID Mesyuarat: {state.meeting.meeting_id} · Dicipta pada {formatDateTime(state.meeting.created_at)}</p>
    {correctionTarget && <AdminAttendanceCorrectionModal target={correctionTarget} onClose={() => setCorrectionTarget(null)} onSaved={async (reason, note) => {
      await correctAttendanceAsStaff({ recordId: correctionTarget.record.record_id, action: correctionTarget.action, reason, note });
      showToast(correctionTarget.action === 'INVALIDATE' ? 'Kehadiran dibatalkan.' : 'Kehadiran dipulihkan.', 'success');
      setCorrectionTarget(null);
      await load();
    }} />}
  </Modal>;
}

function AdminPermanentMeetingDeleteModal({ meetingId, onClose, onDeleted }: {
  meetingId: string;
  onClose: () => void;
  onDeleted: (meetingId: string) => Promise<void>;
}) {
  const [preview, setPreview] = useState<AdminMeetingDeletePreview | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void adminPreviewMeetingDeleteV19_1(meetingId)
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((loadError) => {
        if (!cancelled) setError(errorMessage(loadError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [meetingId]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!preview?.safe_to_delete || confirmation !== 'PADAM' || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await adminDeleteMeetingPermanentlyV19_1(meetingId);
      await onDeleted(result.meeting_id);
    } catch (deleteError) {
      setError(errorMessage(deleteError));
      setBusy(false);
    }
  };

  const close = () => {
    if (!busy) onClose();
  };

  return <Modal title="Padam mesyuarat secara kekal?" onClose={close}>
    {loading ? <Spinner label="Sedang menyediakan pratonton pemadaman…" /> : !preview ? <div className="permanent-delete-error" role="alert"><strong>Pratonton tidak dapat dimuatkan.</strong><p>{error}</p><div className="form-actions"><button type="button" className="button button-ghost" onClick={close}>Kembali</button></div></div> : <form className="form-stack" onSubmit={submit}>
      <p className="permanent-delete-warning">Tindakan ini akan memadam mesyuarat serta semua rekod berkaitan dan tidak boleh dibuat asal.</p>

      <div className="permanent-delete-meeting-summary">
        <strong>{preview.meeting.title}</strong>
        <span>{formatDate(preview.meeting.meeting_date)} · {formatTime(preview.meeting.meeting_time)}</span>
        <span>{preview.meeting.location || 'Lokasi tidak dinyatakan'} · {preview.meeting.status}</span>
      </div>

      <div className="permanent-delete-counts" aria-label="Ringkasan rekod yang akan dipadam">
        <article><span>Kehadiran</span><strong>{preview.dependency_counts.attendance}</strong></article>
        <article><span>Ahli dijemput</span><strong>{preview.dependency_counts.expected_attendees}</strong></article>
        <article><span>Co-Host</span><strong>{preview.dependency_counts.meeting_cohosts}</strong></article>
        <article><span>Tempahan bilik</span><strong>{preview.dependency_counts.room_reservations}</strong></article>
        <article><span>Pengesahan rasmi</span><strong>{preview.dependency_counts.attendance_record_verifications}</strong></article>
        <article><span>Rekod audit berkaitan</span><strong>{preview.dependency_counts.attendance_corrections + preview.dependency_counts.expected_attendee_disposition_events + preview.dependency_counts.meeting_cancellations}</strong></article>
      </div>

      {preview.meeting.series_id ? <p className="permanent-delete-series-note">{preview.series.is_last_occurrence
        ? 'Ini ialah kejadian terakhir dalam siri. Rekod siri yang menjadi yatim juga akan dipadam.'
        : `Hanya kejadian ini akan dipadam. ${preview.series.sibling_occurrence_count} kejadian lain dalam siri akan dikekalkan tanpa perubahan nombor turutan.`}</p> : null}

      {preview.blockers.length ? <div className="permanent-delete-blocker" role="alert"><strong>Mesyuarat ini belum selamat untuk dipadam.</strong>{preview.blockers.map((blocker) => <p key={blocker.code}>{blocker.message}</p>)}</div> : null}
      {error ? <p className="form-validation" role="alert">{error}</p> : null}

      <label className="field"><span>Taip <strong>PADAM</strong> untuk mengesahkan</span><input autoFocus value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="off" spellCheck={false} disabled={busy || !preview.safe_to_delete} /></label>
      <div className="form-actions"><button type="button" className="button button-ghost" onClick={close} disabled={busy}>Kembali</button><button type="submit" className="button button-danger" disabled={busy || !preview.safe_to_delete || confirmation !== 'PADAM'}>{busy ? 'Sedang memadam…' : 'Padam Secara Kekal'}</button></div>
    </form>}
  </Modal>;
}

function AdminAttendanceCorrectionModal({ target, onClose, onSaved }: {
  target: { record: AdminMeetingState['attendance'][number]; action: 'INVALIDATE' | 'RESTORE' };
  onClose: () => void;
  onSaved: (reason?: 'Accidental scan' | 'Wrong meeting' | 'Test entry' | 'Other', note?: string) => Promise<void>;
}) {
  const [reason, setReason] = useState<'Accidental scan' | 'Wrong meeting' | 'Test entry' | 'Other'>('Accidental scan');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();
  const restoring = target.action === 'RESTORE';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!restoring && reason === 'Other' && !note.trim()) {
      showToast('Masukkan nota penerangan apabila sebabnya Lain-lain.', 'error');
      return;
    }
    setBusy(true);
    try { await onSaved(restoring ? undefined : reason, note.trim() || undefined); }
    catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); }
  };

  return <Modal title={restoring ? 'Pulihkan Kehadiran' : 'Batalkan Kehadiran'} onClose={onClose}><form className="form-stack" onSubmit={submit}>
    <p className="muted">{restoring ? 'Pulihkan rekod kehadiran ini sebagai sah.' : 'Cipta rekod pembatalan audit yang hanya ditambah.'} Rekod asal untuk <strong>{target.record.snapshot_name}</strong> akan kekal tidak berubah.</p>
    {!restoring && <label className="field"><span>Sebab</span><select value={reason} onChange={(event) => setReason(event.target.value as typeof reason)}><option value="Accidental scan">Imbasan tersilap</option><option value="Wrong meeting">Mesyuarat tersilap</option><option value="Test entry">Entri ujian</option><option value="Other">Lain-lain</option></select></label>}
    <label className="field"><span>Nota {!restoring && reason === 'Other' ? <em>(Wajib bagi Lain-lain)</em> : <em>(Pilihan)</em>}</span><textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} rows={3} required={!restoring && reason === 'Other'} /></label>
    <div className="form-actions"><button type="button" className="button button-ghost" onClick={onClose}>Batal</button><button className={restoring ? 'button button-primary' : 'button button-danger'} disabled={busy || (!restoring && reason === 'Other' && !note.trim())}>{busy ? 'Sedang menyimpan…' : restoring ? 'Pulihkan kehadiran' : 'Batalkan kehadiran'}</button></div>
  </form></Modal>;
}
